import { useState } from 'react';
import { Pause, Play, Gauge, Lock } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import { BottomSheet } from '@/components/ui/BottomSheet';
import type { SpeedMultiplier } from '@/state/types';

const SPEEDS: SpeedMultiplier[] = [1, 2, 5, 10];

/**
 * Phase 11.1.2 (N4) — Pause-Button funktional in allen Modi.
 *   Realistic erlaubt Pause mit Punkt-Strafe (Phase 7.4.8).
 *   Klassik / Realistic: 2 freie Pausen pro Spieljahr, danach 50 000 €.
 *   Sandbox: unbegrenzt.
 *
 * Phase 11.6 — Speed-Multiplier (1×/2×/5×/10×) nur in Sandbox sichtbar. In
 * Klassik / Realistic läuft die Simulation in Echtzeit (1×).
 */
export function SpeedControl() {
  const speedMultiplier = useGameStore((s) => s.speedMultiplier);
  const setSpeedMultiplier = useGameStore((s) => s.setSpeedMultiplier);
  const paused = useGameStore((s) => s.paused);
  const togglePause = useGameStore((s) => s.togglePause);
  const mode = useGameStore((s) => s.mode);
  const pauseUsage = useGameStore((s) => s.pauseUsage);
  const viewport = useViewport();

  const isSandbox = mode === 'sandbox';
  const showSpeedSwitcher = isSandbox;

  const [sheetOpen, setSheetOpen] = useState(false);

  // Phase 9.6.1 — Pause budget hint.
  const freeLeft =
    isSandbox ? Infinity : Math.max(0, 2 - (pauseUsage?.freePausesUsed ?? 0));
  const isPaid = !isSandbox && freeLeft <= 0;
  const pauseTitle = paused
    ? 'Fortsetzen'
    : isSandbox
      ? 'Pausieren (unbegrenzt)'
      : freeLeft > 0
        ? `Pausieren (${freeLeft} kostenlos übrig)`
        : 'Kostenpflichtige Pause: 50.000 €';

  if (viewport === 'mobile') {
    return (
      <>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label="Spielsteuerung"
          className="pointer-events-auto fixed right-4 top-3 z-30 flex h-12 min-w-[60px] items-center justify-center gap-1.5 rounded-full border border-white/10 px-3 text-sm font-bold shadow-xl backdrop-blur-md"
          style={{ background: 'rgba(20, 24, 36, 0.9)' }}
        >
          {paused ? (
            <Pause className="h-3.5 w-3.5 text-accent-dispatch" aria-hidden />
          ) : (
            <Gauge className="h-3.5 w-3.5 text-accent-dispatch" aria-hidden />
          )}
          <span className="tabular-nums">
            {paused ? 'Pause' : isSandbox ? `${speedMultiplier}×` : '1×'}
          </span>
        </button>
        <BottomSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Spielsteuerung"
        >
          <div className="space-y-4 px-5 pb-6 pt-2">
            {showSpeedSwitcher ? (
              <div className="flex items-center gap-1 rounded-full bg-bg-elevated/40 p-1">
                {SPEEDS.map((sp) => (
                  <button
                    key={sp}
                    type="button"
                    onClick={() => setSpeedMultiplier(sp)}
                    aria-pressed={speedMultiplier === sp}
                    className={[
                      'flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 text-sm font-semibold transition',
                      speedMultiplier === sp
                        ? 'bg-accent-dispatch text-white shadow'
                        : 'text-fg-muted',
                    ].join(' ')}
                  >
                    {sp}×
                  </button>
                ))}
              </div>
            ) : (
              <p className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated/40 px-3 py-2 text-xs text-fg-muted">
                <Lock className="h-3 w-3 flex-shrink-0" aria-hidden />
                Spielgeschwindigkeit nur in Sandbox einstellbar — andere Modi laufen in Echtzeit.
              </p>
            )}
            <button
              type="button"
              onClick={togglePause}
              className={[
                'flex w-full min-h-[48px] items-center justify-center gap-2 rounded-full text-sm font-semibold transition',
                paused
                  ? 'bg-accent-dispatch text-white'
                  : isPaid
                    ? 'bg-amber-500/30 text-amber-200'
                    : 'bg-bg-elevated text-fg-primary',
              ].join(' ')}
            >
              {paused ? (
                <>
                  <Play className="h-4 w-4" aria-hidden />
                  Fortsetzen
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4" aria-hidden />
                  {isSandbox
                    ? 'Pausieren'
                    : freeLeft > 0
                      ? `Pausieren (${freeLeft} frei)`
                      : 'Pause kostet 50.000 €'}
                </>
              )}
            </button>
          </div>
        </BottomSheet>
      </>
    );
  }

  // Tablet/Desktop layout.
  const positionClass =
    viewport === 'tablet'
      ? 'bottom-4 left-1/2 -translate-x-1/2'
      : 'bottom-4 right-4';

  return (
    <div
      className={[
        'pointer-events-auto fixed z-30 flex items-center gap-1 rounded-full border border-white/5 p-1 shadow-xl backdrop-blur-md',
        positionClass,
      ].join(' ')}
      style={{ background: 'rgba(20, 24, 36, 0.9)' }}
    >
      {showSpeedSwitcher && (
        <div className="flex items-center gap-0.5 rounded-full bg-bg-elevated/40 p-0.5">
          {SPEEDS.map((sp) => (
            <button
              key={sp}
              type="button"
              onClick={() => setSpeedMultiplier(sp)}
              aria-label={`Geschwindigkeit ${sp}x`}
              aria-pressed={speedMultiplier === sp}
              className={[
                'rounded-full px-3 py-1 text-xs font-semibold transition',
                speedMultiplier === sp
                  ? 'bg-accent-dispatch text-white shadow'
                  : 'text-fg-muted hover:text-fg-primary',
              ].join(' ')}
            >
              {sp}×
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={togglePause}
        aria-label={paused ? 'Fortsetzen' : 'Pause'}
        title={pauseTitle}
        className={[
          'relative flex h-8 w-8 items-center justify-center rounded-full transition',
          paused
            ? 'bg-accent-dispatch text-white'
            : isPaid
              ? 'bg-amber-500/30 text-amber-200 hover:bg-amber-500/40'
              : 'bg-bg-elevated/40 text-fg-primary hover:bg-bg-elevated',
        ].join(' ')}
      >
        {paused ? (
          <Play className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <Pause className="h-3.5 w-3.5" aria-hidden />
        )}
        {!paused && !isSandbox && (
          <span
            className={[
              'absolute -bottom-1 -right-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full border border-bg-secondary text-[8px] font-bold tabular-nums',
              isPaid ? 'bg-amber-500 text-bg-primary' : 'bg-status-available text-bg-primary',
            ].join(' ')}
          >
            {isPaid ? '€' : freeLeft}
          </span>
        )}
      </button>
    </div>
  );
}
